import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const TermsDialog = ({ open, onClose, onAgreeReached }) => {
  const [terms, setTerms] = useState(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setReachedEnd(false);
    api.get("/legal/terms").then(({ data }) => setTerms(data)).catch((e) => setTerms({ error: errMsg(e) }));
  }, [open]);

  // Defensive: if the terms fit without a scrollbar, unlock acceptance once rendered.
  useEffect(() => {
    if (!terms?.sections) return;
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 8) {
      setReachedEnd(true);
      onAgreeReached?.();
    }
  }, [terms, onAgreeReached]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 8 || el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setReachedEnd(true);
      onAgreeReached?.();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="terms-dialog">
      <div className="bg-white rounded-md w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-[#E5E7EB]">
          <div>
            <h2 className="font-heading text-lg font-bold text-[#111827]">Terms &amp; Conditions</h2>
            <p className="text-xs text-[#6B7280] mt-0.5">
              LabTrack&trade; · PathLete India Pvt. Ltd. · version {terms?.version || "1.0"} ·
              updated {terms?.updated || ""}
            </p>
          </div>
          <button onClick={onClose} data-testid="terms-close-btn" className="p-1 text-[#6B7280] hover:text-[#111827]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div ref={scrollRef} onScroll={onScroll} data-testid="terms-scroll-area"
          className="overflow-y-auto p-5 space-y-5 text-sm text-[#111827]">
          {!terms && <p className="text-[#6B7280]">Loading terms…</p>}
          {terms?.error && <p className="text-[#C92A2A]">{terms.error}</p>}
          {(terms?.sections || []).map((s) => (
            <div key={s.title}>
              <h3 className="font-heading font-semibold text-[#111827]">{s.title}</h3>
              <p className="text-[13px] leading-relaxed text-[#374151] mt-1">{s.body}</p>
            </div>
          ))}
          {terms?.sections && (
            <p className="text-xs text-[#6B7280] border-t border-[#E5E7EB] pt-4" data-testid="terms-end-marker">
              End of Terms &amp; Conditions. You may now accept them and submit your application.
            </p>
          )}
        </div>

        <div className="p-4 border-t border-[#E5E7EB] flex items-center justify-between gap-3">
          <p className="text-xs text-[#6B7280]">
            {reachedEnd ? "You have read the full document." : "Scroll to the end to enable acceptance."}
          </p>
          <Button onClick={onClose} disabled={!reachedEnd} data-testid="terms-done-btn"
            className="bg-[#002FA7] hover:bg-[#00237C] text-white disabled:opacity-40">
            I have read the terms
          </Button>
        </div>
      </div>
    </div>
  );
};
